/*LIBRARY*/
import { useEffect, useState, useMemo, useCallback } from "react";
import { connect } from "react-redux";
import { useLocation } from "react-router-dom";
import { EditorState, convertFromRaw } from "draft-js";
import Editor from "draft-js-plugins-editor";

/*REDUX*/
import {
  set_value_editor,
  get_public_folder_list,
  get_public_doc_list,
} from "../../App/actions/editor_actions";

/*FUNCTION*/
import { get_media_block_renderer } from "../../Components/common/plugins/mediaBlockRenderer";

/*CONST*/
const styleMap = {
  CODE: {
    backgroundColor: "rgba(0, 0, 0, 0.05)",
    fontFamily: '"Inconsolata", "Menlo", "Consolas", monospace',
    fontSize: 16,
    padding: 2,
  },
  BOLD: { fontWeight: 700, color: "#000" },
  ITALIC: { fontStyle: "italic", color: "#333" },
  UNDERLINE: { textDecoration: "underline", textDecorationColor: "#000" },
};

const getBlockStyle = (block) => {
  switch (block.getType()) {
    case "blockquote":
      return "RichEditor-blockquote";
    case "center":
      return "RichEditor-center";
    default:
      return null;
  }
};

/**
 * SUB-COMPONENT: SingleDocContent
 */
const SingleDocContent = ({
  doc,
  styleMap,
  getBlockStyle,
  custom_block_renderer,
}) => {
  const [editorState, setEditorState] = useState(EditorState.createEmpty());

  useEffect(() => {
    if (doc?.editorState) {
      try {
        const content_state = convertFromRaw(JSON.parse(doc.editorState));
        setEditorState(EditorState.createWithContent(content_state));
      } catch (error) {
        setEditorState(EditorState.createEmpty());
      }
    }
  }, [doc]);

  if (!doc?.editorState) return null;

  return (
    <div className="RichEditor-editor">
      <Editor
        blockRendererFn={custom_block_renderer}
        blockStyleFn={getBlockStyle}
        customStyleMap={styleMap}
        editorState={editorState}
        readOnly={true}
        onChange={() => {}}
      />
    </div>
  );
};

/**
 * MAIN COMPONENT: DOWNLOAD_ALL
 */
const DOWNLOAD_ALL = ({
  editor_reducer,
  get_public_folder_list,
  get_public_doc_list,
}) => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const language = queryParams.get("language") || "ina";

  const { blog_list, parent_object } = editor_reducer;
  const [is_ready, set_is_ready] = useState(false);

  useEffect(() => {
    get_public_folder_list(language);
    get_public_doc_list(language);
  }, [language, get_public_folder_list, get_public_doc_list]);

  const sorted_data = useMemo(() => {
    const folders = parent_object?.sub_brackets || [];
    if (folders.length === 0 || blog_list.length === 0) return [];

    return folders.map((folder) => {
      const docs = blog_list.filter((blog) => blog.sub_bracket === folder.link);
      return { ...folder, docs: docs };
    });
  }, [parent_object, blog_list]);

  const handle_image_click = useCallback(() => {}, []);
  const custom_block_renderer = useMemo(
    // Pass 'true' here to activate the hiding logic
    () => get_media_block_renderer(handle_image_click, true),
    [handle_image_click]
  );

  // Automate Print
  useEffect(() => {
    if (sorted_data.length > 0 && !is_ready) {
      const timer = setTimeout(() => {
        set_is_ready(true);
        window.print();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [sorted_data, is_ready]);

  return (
    <main className="pdf-container">
      {!is_ready && (
        <div
          className="no-print"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "white",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
          }}
        >
          <h2>Preparing PDF...</h2>
        </div>
      )}

      {/* --- PAGE 1: COVER PAGE --- */}
      <section className="print-page cover-page">
        <div className="cover-content" style={{ marginTop: "30%" }}>
          <h1
            style={{
              fontSize: "100px",
              fontWeight: "900",
              margin: 0,
              lineHeight: 1,
            }}
          >
            DOC
          </h1>
          <p style={{ fontSize: "24px", marginTop: "20px", color: "#555" }}>
            Complete Documentation
          </p>
          <p style={{ fontSize: "16px", marginTop: "10px", color: "#999" }}>
            {new Date().toLocaleDateString()}
          </p>
        </div>
      </section>

      {/* --- PAGE 2: TABLE OF CONTENTS --- */}
      <section className="print-page toc-page">
        <h2
          style={{
            borderBottom: "2px solid #000",
            paddingBottom: "10px",
            marginBottom: "30px",
          }}
        >
          Table of Contents
        </h2>
        <div className="toc-list">
          {sorted_data.map((folder, index) => (
            <div key={index} style={{ marginBottom: "20px" }}>
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "bold",
                  margin: "0 0 10px 0",
                }}
              >
                {index + 1}. {folder.name}
              </h3>
              <ul style={{ paddingLeft: "25px", listStyle: "none", margin: 0 }}>
                {folder.docs.map((doc, docIndex) => (
                  <li
                    key={doc._id}
                    style={{
                      borderBottom: "1px dotted #ccc",
                      padding: "5px 0",
                    }}
                  >
                    <a href={`#doc-${doc._id}`} className="toc-link">
                      <span>
                        {index + 1}.{docIndex + 1} &nbsp; {doc.title}
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* --- PAGE NEXT: CONTENT --- */}
      <section className="content-wrapper">
        {sorted_data.map((folder, fIndex) => (
          <div key={folder.link}>
            {folder.docs.map((doc, dIndex) => (
              <article
                key={doc._id}
                id={`doc-${doc._id}`}
                className="print-page doc-content"
              >
                <header style={{ marginBottom: "30px" }}>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#666",
                      textTransform: "uppercase",
                    }}
                  >
                    {folder.name}
                  </div>
                  <h1
                    style={{
                      fontSize: "28px",
                      fontWeight: "bold",
                      margin: "10px 0 20px 0",
                      borderBottom: "1px solid #eee",
                      paddingBottom: "20px",
                    }}
                  >
                    {doc.title}
                  </h1>
                </header>
                <SingleDocContent
                  doc={doc}
                  styleMap={styleMap}
                  getBlockStyle={getBlockStyle}
                  custom_block_renderer={custom_block_renderer}
                />
              </article>
            ))}
          </div>
        ))}
      </section>

      <style>{`
        /* --- HIDE EXTERNAL NAVS --- */
        #navbarDefault, .top_nav, .sidebar_home { display: none !important; }

        /* --- SCREEN (PREVIEW) --- */
        body {
            background-color: #f5f5f5;
        }
        .pdf-container {
            max-width: 210mm;
            margin: 30px auto;
            background: white;
            padding: 40px;
            box-shadow: 0 0 10px rgba(0,0,0,0.1);
            min-height: 100vh;
        }
        .toc-link { text-decoration: none; color: black; display: block; width: 100%; }
        .toc-link:hover { text-decoration: underline; }

        /* --- PRINT (DEFAULT MARGINS) --- */
        @media print {
            body {
                background-color: white;
            }
            .pdf-container {
                width: 100%;
                max-width: 100%;
                margin: 0;
                padding: 0;
                box-shadow: none;
            }
            .no-print { display: none !important; }

            /* Page Breaks */
            .cover-page {
                text-align: center;
                height: 90vh; /* Ensures cover takes up most of the first page */
                page-break-after: always;
                break-after: page;
            }
            .toc-page {
                page-break-after: always;
                break-after: page;
            }
            .doc-content {
                page-break-before: always;
                break-before: page;
                margin-bottom: 2cm;
            }

            /* Content Safe-guards */
            a { text-decoration: none !important; color: black !important; }
            h1, h2, h3, h4, h5, img, figure, table { page-break-inside: avoid; }
            
            /* DraftJS cleanup */
            .public-DraftEditor-content { min-height: auto; }
        }
      `}</style>
    </main>
  );
};

const mapStateToProps = (state) => ({ editor_reducer: state.editor_reducer });

export default connect(mapStateToProps, {
  set_value_editor,
  get_public_folder_list,
  get_public_doc_list,
})(DOWNLOAD_ALL);
